import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  deleteField,
  type UpdateData,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import { normalizeInstagramMediaUrl } from '@/lib/instagramMedia'
import type { InstagramReel } from '@/types'

const col = collection(db, COLLECTIONS.instagramReels)

function mapReel(id: string, data: Record<string, unknown>): InstagramReel {
  return {
    id,
    url: (data.url as string) || '',
    title: (data.title as string) || undefined,
    thumbnail: (data.thumbnail as string) || undefined,
    enabled: data.enabled !== false,
    order: Number(data.order) || 0,
    createdAt: (data.createdAt as string) || new Date().toISOString(),
    updatedAt: (data.updatedAt as string) || new Date().toISOString(),
  }
}

export async function fetchInstagramReels(): Promise<InstagramReel[]> {
  const q = query(col, orderBy('order', 'asc'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => mapReel(d.id, d.data()))
}

export async function createInstagramReel(
  data: Omit<InstagramReel, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString()
  const normalized = normalizeInstagramMediaUrl(data.url)
  if (!normalized) {
    throw new Error('Invalid Instagram link')
  }
  const ref = await addDoc(col, {
    ...data,
    url: normalized,
    ...(data.thumbnail ? { thumbnail: data.thumbnail } : {}),
    createdAt: now,
    updatedAt: now,
  })
  return ref.id
}

export async function updateInstagramReel(
  id: string,
  data: Partial<Omit<InstagramReel, 'id' | 'createdAt'>>
): Promise<void> {
  const { thumbnail, ...rest } = data
  const payload: UpdateData<Record<string, unknown>> = {
    ...rest,
    updatedAt: new Date().toISOString(),
  }
  if (data.url) {
    const normalized = normalizeInstagramMediaUrl(data.url)
    if (!normalized) {
      throw new Error('Invalid Instagram link')
    }
    payload.url = normalized
  }
  if (thumbnail === '') {
    payload.thumbnail = deleteField()
  } else if (thumbnail) {
    payload.thumbnail = thumbnail
  }
  await updateDoc(doc(db, COLLECTIONS.instagramReels, id), payload)
}

export async function deleteInstagramReel(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.instagramReels, id))
}
